<template>
  <div class="ocr-page-section">
    <OcrPageShimmer v-if="loadingReports" variant="reports" />
    <template v-else>
      <div class="ocr-report-workspace">
        <aside class="ocr-report-sidebar ocr-panel">
          <div class="ocr-field">
            <label class="profile-menu-label">Report</label>
            <v-select
              v-model="selectedReport"
              :items="reports"
              variant="outlined"
              density="comfortable"
              hide-details
              menu-icon="mdi-chevron-down"
              class="ocr-report-report-select"
              @update:model-value="selectReport"
            />
          </div>
          <p v-if="mappingSummary" class="ocr-mapping-summary">
            Mapped {{ mappingSummary.mapped_tables }} / {{ mappingSummary.total_tables }} tables
          </p>
          <OcrReportSidebarNav
            :filename="loadedReport"
            :tables="tables"
            :selected-table-index="selectedTableIndex"
            :page-index="previewPageIndex"
            @select-table="selectedTableIndex = $event"
            @go-page="onGoPage"
            @open-map-tab="reportsTab = 'map'"
          />
        </aside>

        <div class="ocr-report-main">
          <v-alert v-if="selectError" type="error" variant="tonal" density="compact" class="mb-2">
            {{ selectError }}
          </v-alert>
          <div class="ocr-tabbed-stack">
            <OcrTabs v-model="reportsTab">
              <v-tab value="reports">Reports</v-tab>
              <v-tab value="tables">Report Tables</v-tab>
              <v-tab value="map">Map Report Tables</v-tab>
              <v-tab value="samples">Manage Samples</v-tab>
            </OcrTabs>

            <v-window v-model="reportsTab" class="ocr-tab-window">
              <v-window-item value="reports">
                <div class="ocr-report-split" :class="{ 'ocr-report-split--doc-collapsed': docCollapsed }">
                  <OcrReportDocument :filename="loadedReport" @update:collapsed="docCollapsed = $event" />
                  <OcrReportPagePreview
                    v-model:page-index="previewPageIndex"
                    :filename="loadedReport"
                    :max-page="maxPageIndex"
                    :highlight-tables="true"
                    :active-table-index="selectedTableIndex"
                  />
                </div>
              </v-window-item>

              <v-window-item value="tables">
                <OcrReportTablesPanel
                  :filename="loadedReport"
                  :tables="tables"
                  :table-types="tableTypes"
                  :selected-table-index="selectedTableIndex"
                  :table-detail="selectedTableDetail"
                  :page-index="previewPageIndex"
                  :max-page-index="maxPageIndex"
                  @select-table="selectedTableIndex = $event"
                  @go-page="onGoPage"
                  @added="onTableAdded"
                />
              </v-window-item>

              <v-window-item value="map">
                <OcrReportMapPanel
                  v-model:page-index="previewPageIndex"
                  :filename="loadedReport"
                  :table-index="selectedTableIndex"
                  :table-types="tableTypes"
                  :max-page-index="maxPageIndex"
                  @updated="onTableUpdated"
                />
              </v-window-item>

              <v-window-item value="samples">
                <OcrReportSamples :filename="loadedReport" />
              </v-window-item>
            </v-window>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrPageShimmer from "@/components/layout/OCR/OcrPageShimmer.vue";
import OcrTabs from "@/components/layout/OCR/OcrTabs.vue";
import OcrReportDocument from "@/components/layout/OCR/OcrReportDocument.vue";
import OcrReportMapPanel from "@/components/layout/OCR/OcrReportMapPanel.vue";
import OcrReportPagePreview from "@/components/layout/OCR/OcrReportPagePreview.vue";
import OcrReportSidebarNav from "@/components/layout/OCR/OcrReportSidebarNav.vue";
import OcrReportSamples from "@/components/layout/OCR/OcrReportSamples.vue";
import OcrReportTablesPanel from "@/components/layout/OCR/OcrReportTablesPanel.vue";

export default {
  name: "OcrReports",
  components: {
    OcrPageShimmer,
    OcrTabs,
    OcrReportDocument,
    OcrReportMapPanel,
    OcrReportPagePreview,
    OcrReportSidebarNav,
    OcrReportSamples,
    OcrReportTablesPanel,
  },
  data() {
    return {
      loadingReports: true,
      reportsTab: "reports",
      reports: [],
      selectedReport: null,
      loadedReport: null,
      selectError: "",
      selectedTableIndex: null,
      previewPageIndex: 0,
      tables: [],
      tableTypes: [],
      selectedTableDetail: null,
      mappingSummary: null,
      navPageCount: 0,
      docCollapsed: false,
    };
  },
  computed: {
    maxPageIndex() {
      if (this.navPageCount > 0) return this.navPageCount - 1;
      if (!this.tables.length) return 0;
      return Math.max(...this.tables.map((t) => t.page ?? 0));
    },
  },
  watch: {
    selectedTableIndex: {
      handler(idx) {
        if (idx == null || !this.selectedReport) {
          this.selectedTableDetail = null;
          return;
        }
        const t = this.tables.find((x) => x.index === idx);
        if (t?.page != null) this.previewPageIndex = t.page;
        this.loadTableDetail(idx);
      },
    },
  },
  async mounted() {
    await Promise.all([this.loadReports(), this.loadTableTypes()]);
    await this.applyQueryNavigation();
  },
  methods: {
    async loadTableTypes() {
      try {
        const { data } = await ocrApi.get("api/v1/reports/meta/table-types");
        this.tableTypes = data.table_types || [];
      } catch {
        this.tableTypes = [];
      }
    },
    matchReportName(stem) {
      if (!stem) return null;
      const exact = this.reports.find((r) => r === stem);
      if (exact) return exact;
      const withPdf = this.reports.find((r) => r === `${stem}.pdf`);
      if (withPdf) return withPdf;
      return this.reports.find((r) => String(r).split(".")[0] === stem) || null;
    },
    async applyQueryNavigation() {
      const q = this.$route.query || {};
      const stem = q.file ? String(q.file) : "";
      const tab = q.tab ? String(q.tab) : "";
      if (tab && ["reports", "tables", "map", "samples"].includes(tab)) {
        this.reportsTab = tab;
      }
      const matched = this.matchReportName(stem);
      if (matched) {
        this.selectedReport = matched;
        await this.selectReport(matched);
      }
      if (q.page != null && Number(q.page) >= 0) {
        const page = Number(q.page);
        this.previewPageIndex = page;
        const tableNum = q.table != null ? Number(q.table) : null;
        const byTable = this.tables.find(
          (t) => t.page === page && (tableNum == null || t.page_table_number === tableNum || t.index === tableNum),
        );
        const onPage = byTable || this.tables.find((t) => t.page === page);
        if (onPage) this.selectedTableIndex = onPage.index;
      }
    },
    async loadReports() {
      this.loadingReports = true;
      try {
        const { data } = await ocrApi.get("api/v1/reports");
        this.reports = data.reports || [];
        if (data.selected) {
          this.selectedReport = data.selected;
          await this.selectReport(data.selected);
        }
      } finally {
        this.loadingReports = false;
      }
    },
    onGoPage(page) {
      this.previewPageIndex = page;
      const onPage = this.tables.find((t) => t.page === page);
      if (onPage && this.selectedTableIndex !== onPage.index) {
        this.selectedTableIndex = onPage.index;
      }
    },
    async selectReport(name) {
      if (!name) return;
      const changed = this.loadedReport !== name;
      this.selectError = "";
      try {
        const { data } = await ocrApi.post("api/v1/reports/select", { filename: name });
        if (changed) {
          this.selectedTableIndex = null;
          this.previewPageIndex = 0;
          this.selectedTableDetail = null;
          this.tables = [];
        }
        this.loadedReport = name;
        await this.refreshTables(name, data.tables);
        await this.loadNavMeta(name);
      } catch (e) {
        this.selectError = formatOcrApiError(e, "Could not open this report.");
        if (this.loadedReport && this.selectedReport !== this.loadedReport) {
          this.selectedReport = this.loadedReport;
        }
      }
    },
    async loadNavMeta(name) {
      try {
        const { data } = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/navigation`);
        this.navPageCount = data.page_count || 0;
      } catch {
        this.navPageCount = 0;
      }
    },
    async refreshTables(name, fallbackTables) {
      try {
        const detail = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/tables`);
        this.tables = (detail.data.tables || []).map((t, index) => ({
          index: t.index ?? index,
          page: t.page,
          table_type: t.table_type,
          page_table_number: t.page_table_number,
          status:
            t.table_type && !["Unknown", "Select Table Type", "Ignore"].includes(t.table_type)
              ? "mapped"
              : "unmapped",
        }));
      } catch {
        this.tables = fallbackTables || [];
      }
      if (this.tables.length && this.selectedTableIndex == null) {
        this.selectedTableIndex = this.tables[0].index;
      }
      const map = await ocrApi.get(`api/v1/reports/${encodeURIComponent(name)}/mapping/summary`);
      this.mappingSummary = map.data;
    },
    async loadTableDetail(index) {
      if (!this.selectedReport) return;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.selectedReport)}/tables/${index}`,
        );
        this.selectedTableDetail = data;
      } catch {
        this.selectedTableDetail = null;
      }
    },
    async onTableAdded(index) {
      if (!this.selectedReport) return;
      await this.selectReport(this.selectedReport);
      if (index != null) this.selectedTableIndex = index;
    },
    async onTableUpdated() {
      if (!this.selectedReport) return;
      await this.selectReport(this.selectedReport);
      await this.loadNavMeta(this.selectedReport);
      if (this.selectedTableIndex != null) {
        await this.loadTableDetail(this.selectedTableIndex);
      }
    },
  },
};
</script>
